import React, { useState, useEffect, useRef } from "react"
import { Head, router } from "@inertiajs/react"
import {
  Plus,
  Search,
  MoreHorizontal,
  Calendar,
  Webhook,
  Clock,
  Play,
  Copy,
  Trash2,
  Edit,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  X,
  Users,
  Building2,
  UserCheck,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FilterDropdown } from "@/components/filter-dropdown"
import { useMobile } from "@/hooks/use-mobile"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import { useToast } from "@/hooks/use-toast"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { useUsers } from "@/lib/contexts/user-context"
import MassAssignmentModal from "@/components/workflows/MassAssignmentModal"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useFilterStorage } from "@/hooks/use-filter-storage"

export default function WorkflowsListPage(props) {
  return (
    <>
      <Head title="DME | Workflows" />
      <AuthenticatedLayout>
        <WorkflowsListContent {...props} />
      </AuthenticatedLayout>
    </>
  )
}

const defaultWorkflowFilters = {
  searchQuery: "",
  statusFilter: "all",
  triggerFilter: "all",
  sortColumn: "name",
  sortDirection: "asc",
  rowsPerPage: 10,
  page: 0,
};

function WorkflowsListContent({csrf_token, departments = [], users = [], workflows: serverWorkflows = []}) {
  const isMobile = useMobile()
  const { workflows, createWorkflow, currentWorkflow, deleteWorkflow, setWorkflows, updateWorkflow } = useWorkflow()
  const { currentUser } = useUsers()
  const { toast } = useToast()
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [isNewWorkflow, setIsNewWorkflow] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = useState(null)

  const { filters, updateFilter, updateFilters, resetFilters: resetStoredFilters } = useFilterStorage('workflows-filters', defaultWorkflowFilters);

  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false)

  useEffect(() => {
    if (!Array.isArray(serverWorkflows)) return

    const mappedWorkflows = serverWorkflows.map((item) => ({
      id: item.id.toString(),
      name: item.name,
      description: item.description,
      status: item.status || "draft",
      nodes: [],
      edges: [],
      createdAt: item.created_at,
      lastRun: item.last_run || null,
      triggerType: item.trigger_type || "manual",
      updatedAt: item.updated_at,
      creator: item.owner?.name || "Unknown User",
      viewMode: "graph",
      assignment_type: item.assignment_type || null,
      assigned_user_ids: item.assigned_user_ids || null,
      assigned_department_ids: item.assigned_department_ids || null,
    }))

    setWorkflows(mappedWorkflows)
  }, [serverWorkflows, setWorkflows])

  // Add a local lock to prevent double/triple clicks (persistent across renders)
  const createWorkflowButtonLock = useRef(false);

  // Reset loading state when component unmounts or workflows change
  useEffect(() => {
    if (isCreatingWorkflow) {
      setIsCreatingWorkflow(false)
    }
  }, [workflows, isCreatingWorkflow])

  // Handle column sorting
  const handleSort = (column) => {
    if (filters.sortColumn === column) {
      // Toggle direction if clicking the same column
      updateFilter('sortDirection', filters.sortDirection === "asc" ? "desc" : "asc")
    } else {
      // Set new column and default to ascending
      updateFilters({ sortColumn: column, sortDirection: "asc" })
    }
  }

  // Reset all filters
  const resetFilters = () => {
    resetStoredFilters()
  }

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0
    if (filters.searchQuery) count++
    if (filters.statusFilter !== "all") count++
    if (filters.triggerFilter !== "all") count++
    return count
  }

  // Filter workflows based on search query and active tab
  const filteredWorkflows = workflows.filter((workflow) => {
    // Search filter
    const matchesSearch =
      workflow.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
      (workflow.description?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ?? false) ||
      workflow.id.toLowerCase().includes(filters.searchQuery.toLowerCase())

    // Status filter
    const matchesStatus =
      filters.statusFilter === "all" ||
      (filters.statusFilter === "active" && workflow.status === "active") ||
      (filters.statusFilter === "inactive" && workflow.status === "inactive") ||
      (filters.statusFilter === "draft" && workflow.status === "draft")

    // Trigger filter - get trigger type from the first node if it exists
    const triggerType = workflow.triggerType || workflow.nodes[0]?.data?.triggerType || "manual"
    const matchesTrigger = filters.triggerFilter === "all" || triggerType === filters.triggerFilter

    // Date range filter (using created date)
    const workflowDate = new Date(workflow.createdAt)
    const matchesDateRange =
      true // Removed date range filter

    return matchesSearch && matchesStatus && matchesTrigger && matchesDateRange
  })

  // Sort the filtered workflows
  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    let comparison = 0

    switch (filters.sortColumn) {
      case "name":
        comparison = a.name.localeCompare(b.name)
        break
      case "status":
        comparison = a.status.localeCompare(b.status)
        break
      case "triggerType":
        const aTrigger = a.triggerType || a.nodes[0]?.data?.triggerType || "manual"
        const bTrigger = b.triggerType || b.nodes[0]?.data?.triggerType || "manual"
        comparison = aTrigger.localeCompare(bTrigger)
        break
      case "lastRun":
        if (!a.lastRun) return 1
        if (!b.lastRun) return -1
        comparison = new Date(a.lastRun).getTime() - new Date(b.lastRun).getTime()
        break
      case "creator":
        comparison = (a.creator || "").localeCompare(b.creator || "")
        break
      case "created":
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        break
      default:
        comparison = 0
    }

    return filters.sortDirection === "asc" ? comparison : -comparison
  })

  // Paginate the sorted workflows
  const paginatedWorkflows = sortedWorkflows.slice(filters.page * filters.rowsPerPage, filters.page * filters.rowsPerPage + filters.rowsPerPage)

  // Add this function to handle page changes
  const handlePageChange = (newPage) => {
    updateFilter('page', newPage)
  }

  const handleCreateWorkflow = async () => {
    if (createWorkflowButtonLock.current) return;
    createWorkflowButtonLock.current = true;
    setIsCreatingWorkflow(true)
    
    try {
      // Get the current user's name from the user context
      const creator = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "Unknown User";
      const now = new Date()
      const formattedDate = now.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
      const name = `Workflow - ${creator} - ${formattedDate}`
      
      // Create the workflow and get the created workflow object directly
      const newlyCreatedWorkflow = await createWorkflow(name, csrf_token)
      
      // Show the assignment modal for the new workflow instead of redirecting immediately
      if (newlyCreatedWorkflow && newlyCreatedWorkflow.id) {
        setSelectedWorkflow(newlyCreatedWorkflow)
        setIsNewWorkflow(true)
        setShowAssignmentModal(true)
      }
    } catch (error) {
      console.error('Failed to create workflow:', error)
      setIsCreatingWorkflow(false)
      
      // Show error toast
      toast({
        title: "Error",
        description: "Failed to create workflow. Please try again.",
        variant: "destructive",
        className: "bg-white",
      })
    } finally {
      createWorkflowButtonLock.current = false;
    }
  }

  const handleWorkflowClick = (id) => {
    const workflow = workflows.find(w => w.id === id)
    router.visit(`/workflows/${id}`)
  }

  const handleAssignmentClick = (workflow) => {
    setSelectedWorkflow(workflow)
    setIsNewWorkflow(false)
    setShowAssignmentModal(true)
  }

  const handleAssignmentSave = async (workflowId, assignmentData, csrf_token, isNewWorkflow = false) => {
    try {
      const response = await fetch(`/workflows/${workflowId}/assignment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
          'Accept': 'application/json',
        },
        body: JSON.stringify(assignmentData),
      })

      if (!response.ok) {
        throw new Error(`Failed to update assignment: ${response.status}`)
      }

      const result = await response.json()
      
      // Update the workflow in the local state using updateWorkflow
      // This will automatically update both workflows array and currentWorkflow if it's the same workflow
      await updateWorkflow(workflowId, {
        assignment_type: result.workflow.assignment_type,
        assigned_user_ids: result.workflow.assigned_user_ids,
        assigned_department_ids: result.workflow.assigned_department_ids
      }, true, csrf_token) // skipApiCall = true since we already made the API call

      toast({
        title: "Assignment Updated",
        description: "Workflow assignment has been updated successfully.",
        className: "bg-white",
      })

      // If this is a new workflow, redirect to the builder after saving assignment
      if (isNewWorkflow) {
        router.visit(`/workflows/${workflowId}`)
      }
    } catch (error) {
      console.error('Failed to update assignment:', error)
      toast({
        title: "Error",
        description: "Failed to update workflow assignment. Please try again.",
        variant: "destructive",
        className: "bg-white",
      })
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Never"
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    }).format(date)
  }

  const getTriggerIcon = (triggerType) => {
    switch (triggerType) {
      case "webhook":
        return <Webhook className="h-4 w-4 mr-1" />
      case "schedule":
        return <Calendar className="h-4 w-4 mr-1" />
      case "manual":
        return <Play className="h-4 w-4 mr-1" />
      default:
        return null
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge variant="outline" className="bg-green-100 text-green-800">Active</Badge>
      case "inactive":
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Inactive
          </Badge>
        )
      case "draft":
        return <Badge variant="secondary">Draft</Badge>
      default:
        return null
    }
  }

  const getAssignmentDescription = (workflow) => {
    if (!workflow.assignment_type) {
      return 'No assignment'
    }

    switch (workflow.assignment_type) {
      case 'individual':
        const userCount = workflow.assigned_user_ids?.length || 0
        if (userCount === 0) return 'No users assigned'
        return `${userCount} individual user${userCount !== 1 ? 's' : ''}`
      
      case 'department':
        const deptCount = workflow.assigned_department_ids?.length || 0
        if (deptCount === 0) return 'No departments assigned'
        return `${deptCount} department${deptCount !== 1 ? 's' : ''}`
      
      case 'department_manager':
        const managerDeptCount = workflow.assigned_department_ids?.length || 0
        if (managerDeptCount === 0) return 'No departments assigned'
        return `Manager${managerDeptCount !== 1 ? 's' : ''} of ${managerDeptCount} department${managerDeptCount !== 1 ? 's' : ''}`
      
      default:
        return 'Unknown assignment'
    }
  }

  const getAssignmentIcon = (assignmentType) => {
    switch (assignmentType) {
      case 'individual':
        return <Users className="h-4 w-4" />
      case 'department':
        return <Building2 className="h-4 w-4" />
      case 'department_manager':
        return <UserCheck className="h-4 w-4" />
      default:
        return null
    }
  }

  const getAssignmentDetails = (workflow) => {
    if (!workflow.assignment_type) {
      return { text: 'No assignment', icon: null }
    }

    switch (workflow.assignment_type) {
      case 'individual':
        const userCount = workflow.assigned_user_ids?.length || 0
        if (userCount === 0) {
          return { text: 'No users assigned', icon: <Users className="h-4 w-4" /> }
        }
        return { 
          text: `${userCount} individual user${userCount !== 1 ? 's' : ''}`, 
          icon: <Users className="h-4 w-4" /> 
        }
      
      case 'department':
        const deptCount = workflow.assigned_department_ids?.length || 0
        if (deptCount === 0) {
          return { text: 'No departments assigned', icon: <Building2 className="h-4 w-4" /> }
        }
        return { 
          text: `${deptCount} department${deptCount !== 1 ? 's' : ''}`, 
          icon: <Building2 className="h-4 w-4" /> 
        }
      
      case 'department_manager':
        const managerDeptCount = workflow.assigned_department_ids?.length || 0
        if (managerDeptCount === 0) {
          return { text: 'No departments assigned', icon: <UserCheck className="h-4 w-4" /> }
        }
        return { 
          text: `Manager${managerDeptCount !== 1 ? 's' : ''} of ${managerDeptCount} department${managerDeptCount !== 1 ? 's' : ''}`, 
          icon: <UserCheck className="h-4 w-4" /> 
        }
      
      default:
        return { text: 'Unknown assignment', icon: null }
    }
  }

  const getAssignmentTooltipContent = (workflow) => {
    if (!workflow.assignment_type) {
      return <div className="text-sm">No assignment configured</div>
    }

    switch (workflow.assignment_type) {
      case 'individual':
        const assignedUsers = users.filter(user => 
          workflow.assigned_user_ids?.includes(user.id)
        )
        if (assignedUsers.length === 0) {
          return <div className="text-sm">No users assigned</div>
        }
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">Assigned Users:</div>
            <div className="space-y-1">
              {assignedUsers.map(user => (
                <div key={user.id} className="text-sm text-gray-600 dark:text-gray-300">
                  {user.firstName} {user.lastName}
                </div>
              ))}
            </div>
          </div>
        )
      
      case 'department':
        const assignedDepts = departments.filter(dept => 
          workflow.assigned_department_ids?.includes(dept.id)
        )
        if (assignedDepts.length === 0) {
          return <div className="text-sm">No departments assigned</div>
        }
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">Assigned Departments:</div>
            <div className="space-y-1">
              {assignedDepts.map(dept => (
                <div key={dept.id} className="text-sm text-gray-600 dark:text-gray-300">
                  {dept.full_path || dept.name}
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              All users in these departments will be assigned this workflow
            </div>
          </div>
        )
      
      case 'department_manager':
        const managerDepts = departments.filter(dept => 
          workflow.assigned_department_ids?.includes(dept.id)
        )
        if (managerDepts.length === 0) {
          return <div className="text-sm">No departments assigned</div>
        }
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">Department Managers:</div>
            <div className="space-y-2">
              {managerDepts.map(dept => {
                const managers = dept.users?.filter(user => 
                  user.roles?.some(role => role.name === 'manager')
                ) || []
                return (
                  <div key={dept.id} className="space-y-1">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {dept.full_path || dept.name}:
                    </div>
                    {managers.length > 0 ? (
                      <div className="ml-2 space-y-1">
                        {managers.map(manager => (
                          <div key={manager.id} className="text-sm text-gray-600 dark:text-gray-300">
                            {manager.firstName} {manager.lastName}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        No managers found
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      
      default:
        return <div className="text-sm">Unknown assignment type</div>
    }
  }

  // Helper function to render sort indicator
  const renderSortIndicator = (column) => {
    if (filters.sortColumn !== column) return null
    return filters.sortDirection === "asc" ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    )
  }

  return (
    <div className="flex flex-col gap-6 w-full px-4 md:px-6 pb-8 pt-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
        <p className="text-muted-foreground">Create and manage automated workflows for your business processes</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search workflows..."
              value={filters.searchQuery}
              onChange={(e) => updateFilter('searchQuery', e.target.value)}
              className="pl-10 h-9"
            />
          </div>
          
          {isMobile ? (
            <FilterDropdown onReset={resetFilters} count={getActiveFilterCount()}>
              <Select value={filters.statusFilter} onValueChange={(value) => updateFilter('statusFilter', value)}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.triggerFilter} onValueChange={(value) => updateFilter('triggerFilter', value)}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="Trigger Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Triggers</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>

              {getActiveFilterCount() > 0 && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9">
                  <X className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              )}
            </FilterDropdown>
          ) : (
            <>
              <Select value={filters.statusFilter} onValueChange={(value) => updateFilter('statusFilter', value)}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.triggerFilter} onValueChange={(value) => updateFilter('triggerFilter', value)}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Trigger Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Triggers</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>

              {getActiveFilterCount() > 0 && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9">
                  <X className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? "s" : ""} found
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                    Name {renderSortIndicator("name")}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("status")}>
                    Status {renderSortIndicator("status")}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("triggerType")}>
                    Trigger {renderSortIndicator("triggerType")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer" onClick={() => handleSort("lastRun")}>
                    Last Run {renderSortIndicator("lastRun")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer" onClick={() => handleSort("creator")}>
                    Created By {renderSortIndicator("creator")}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell cursor-pointer" onClick={() => handleSort("created")}>
                    Created {renderSortIndicator("created")}
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    Assignment
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedWorkflows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="h-8 w-8 text-muted-foreground" />
                        <h3 className="text-lg font-semibold">No workflows found</h3>
                        <p className="text-sm text-muted-foreground">
                          Try adjusting your search or filter to find what you're looking for.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedWorkflows.map((workflow) => (
                    <TableRow
                      key={workflow.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleWorkflowClick(workflow.id)}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{workflow.name}</div>
                          <div className="text-sm text-muted-foreground">{workflow.description}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(workflow.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          {getTriggerIcon(workflow.triggerType || workflow.nodes[0]?.data?.triggerType || "manual")}
                          <span className="capitalize">{workflow.triggerType || workflow.nodes[0]?.data?.triggerType || "manual"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
                          {formatDate(workflow.lastRun || null)}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{workflow.creator}</TableCell>
                      <TableCell className="hidden lg:table-cell">{formatDate(workflow.createdAt || null)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 cursor-help">
                                {(() => {
                                  const assignmentDetails = getAssignmentDetails(workflow)
                                  return (
                                    <>
                                      {assignmentDetails.icon}
                                      <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {assignmentDetails.text}
                                      </span>
                                    </>
                                  )
                                })()}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm">
                              {getAssignmentTooltipContent(workflow)}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                router.visit(`/workflows/${workflow.id}`)
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                handleAssignmentClick(workflow)
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Assign
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                console.log("Run workflow:", workflow.id)
                              }}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                router.post(`/workflows/${workflow.id}/duplicate`, {}, {
                                  preserveScroll: true,
                                  onSuccess: (page) => {
                                    const duplicatedWorkflowName = page?.props?.flash?.duplicated_workflow_name
                                    toast({
                                      title: "Workflow duplicated",
                                      description: `Created "${duplicatedWorkflowName || `${workflow.name} (copy)`}" successfully.`,
                                      className: "bg-white",
                                    })

                                    router.reload({
                                      only: ['workflows'],
                                      preserveScroll: true,
                                    })
                                  },
                                  onError: (errors) => {
                                    console.error('Failed to duplicate workflow:', errors)
                                    toast({
                                      title: "Error",
                                      description: "Failed to duplicate workflow. Please try again.",
                                      variant: "destructive",
                                      className: "bg-white",
                                    })
                                  },
                                })
                              }}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={async (e) => {
                                e.stopPropagation()
                                try {
                                  await deleteWorkflow(workflow.id, csrf_token)
                                  toast({
                                    title: "Workflow deleted",
                                    description: `Successfully deleted workflow "${workflow.name}"`,
                                    className: "bg-white",
                                  })
                                } catch (error) {
                                  console.error('Failed to delete workflow:', error)
                                  toast({
                                    title: "Error",
                                    description: "Failed to delete workflow. Please try again.",
                                    variant: "destructive",
                                    className: "bg-white",
                                  })
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-2">
              <Select
                value={filters.rowsPerPage.toString()}
                onValueChange={(value) => {
                  updateFilters({ rowsPerPage: Number(value), page: 0 })
                }}
              >
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Rows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 rows</SelectItem>
                  <SelectItem value="20">20 rows</SelectItem>
                  <SelectItem value="50">50 rows</SelectItem>
                  <SelectItem value="100">100 rows</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground">
                Showing <strong>{Math.min(paginatedWorkflows.length, filters.rowsPerPage)}</strong> of{" "}
                <strong>{workflows.length}</strong> workflows
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="icon" onClick={() => handlePageChange(filters.page - 1)} disabled={filters.page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.ceil(sortedWorkflows.length / filters.rowsPerPage) })
                .map((_, index) => (
                  <Button
                    key={index}
                    variant={filters.page === index ? "default" : "outline"}
                    size="sm"
                    className="h-8 w-8 dark:text-white"
                    onClick={() => handlePageChange(index)}
                  >
                    {index + 1}
                  </Button>
                ))
                .slice(Math.max(0, filters.page - 1), Math.min(filters.page + 2, Math.ceil(sortedWorkflows.length / filters.rowsPerPage)))}
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= Math.ceil(sortedWorkflows.length / filters.rowsPerPage) - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mass Assignment Modal */}
      <MassAssignmentModal
        key={selectedWorkflow?.id || 'new'}
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false)
          setIsNewWorkflow(false)
        }}
        onSave={(workflowId, assignmentData, csrf_token) => 
          handleAssignmentSave(workflowId, assignmentData, csrf_token, isNewWorkflow)
        }
        workflow={selectedWorkflow}
        departments={departments}
        users={users}
        csrf_token={csrf_token}
      />
    </div>
  )
}
